import { forwardRef } from 'react';
import { Button as AntdButton } from 'antd';
import clsx from 'clsx';
import styles from './Button.module.css';

/** @typedef {import('../types').SurfaceToken} SurfaceToken */

/** Surfaces dark enough to need light (on-brand) text rather than the default dark text. */
const DARK_SURFACES = new Set(['inverse', 'brand', 'brand-strong']);

/**
 * @param {Object} props - Same shape as antd's own `Button`, forwarded as-is.
 * @param {'large'|'middle'|'small'} [props.size] - `small` renders visually at 32x32 (e.g. for
 *   icon-only buttons) but keeps a 44x44 accessible touch target via an invisible `::before`.
 * @param {SurfaceToken} [props.surface] - Background from the semantic surface token scale
 *   (the same scale `Card` uses), with text color picked automatically for contrast. Overrides
 *   whatever background the button's `type`/`variant`/`color` would otherwise give it.
 * @param {boolean} [props.onDark] - Restyle the button for a dark background: light text/outline for `text`,
 *   `link` and `dashed`, an inverted fill for `primary`, and a translucent white fill for the default button.
 */
const Button = forwardRef(({ className, size, surface, onDark, style, ...props }, ref) => (
    <AntdButton
        ref={ref}
        size={size}
        className={clsx(
            styles.vecButton,
            size === 'small' && styles.vecButtonSmall,
            surface && styles.vecButtonSurface,
            onDark && styles.vecButtonOnDark,
            className
        )}
        style={{
            ...style,
            ...(surface && {
                '--vec-button-surface-bg': `var(--color-surface-${surface})`,
                '--vec-button-surface-color': DARK_SURFACES.has(surface)
                    ? 'var(--color-text-on-brand)'
                    : 'var(--color-text-primary)',
            }),
        }}
        {...props}
    />
));
Button.displayName = 'Button';

export default Button;
